import { useEffect } from "react"
import Head from "next/head"
import { IMAGE_SIZES } from "@/constants/ui"
import MainLayout, { getMainLayout } from "@/layouts/main"
import { AuthPageProps } from "@/types/page"
import { InitProfileProps, InitPublicProfileProps } from "@/types/profile"

import { buildClientAxios } from "@/lib/client-axios"
import cloudflareLoader from "@/lib/cloudflare-loader"
import { MtSSP } from "@/lib/mt-server-side-props"
import { fetchProfileProps, fetchPublicProfileProps } from "@/lib/profile"
import { useRouter } from "@/lib/router"
import { handleCreatorAttributionRedirect } from "@/lib/share-utils"
import { useAppDispatch, useAppStore } from "@/lib/storage/hooks"
import { ProfileTabTypeValues } from "@/lib/storage/profile-slice"
import { initProfile, setTabWithData } from "@/lib/storage/profile-thunks"
import {
  resetPublicProfile,
  selectPublicUser,
} from "@/lib/storage/public-profile-slice"
import { initPublicProfile } from "@/lib/storage/public-profile-thunks"
import { UserApi } from "@/lib/user-api"
import { userDisplayName } from "@/lib/user-utils"
import { singleParam } from "@/lib/utils/url"
import { Profile } from "@/components/profile"
import { PublicProfile } from "@/components/public-profile"

interface ProfilePageProps extends AuthPageProps {
  profileData: InitPublicProfileProps | null
  isOwnProfile: boolean
}

function OwnProfileView({ user }: { user: InitProfileProps["initUser"] }) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const tabParam = singleParam(router.query.tab)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const profileProps = await fetchProfileProps(buildClientAxios())
      if (cancelled) return
      await dispatch(initProfile({ initUser: user, ...profileProps }))
      if (cancelled) return
      const matchedTab = ProfileTabTypeValues.find((t) => t === tabParam)
      if (matchedTab) {
        void dispatch(setTabWithData({ tab: matchedTab }))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dispatch, user, tabParam])

  return <Profile />
}

function PublicProfileView({
  handle,
  profileData,
}: {
  handle: string
  profileData: InitPublicProfileProps | null
}) {
  const dispatch = useAppDispatch()
  const store = useAppStore()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      // blank out a previously-viewed profile right away so it doesn't
      // linger while the fallback fetch runs
      const publicUser = selectPublicUser(store.getState())
      if (publicUser && publicUser.handle !== handle) {
        dispatch(resetPublicProfile())
      }
      // getServerSideProps already fetched the profile data; only fall
      // back to a client fetch when that fetch failed
      const publicProfileProps =
        profileData && profileData.user.handle === handle
          ? profileData
          : await fetchPublicProfileProps(buildClientAxios(), handle)
      if (cancelled) return
      void dispatch(initPublicProfile({ ...publicProfileProps }))
    })()
    return () => {
      cancelled = true
    }
  }, [dispatch, store, handle, profileData])

  return <PublicProfile handle={handle} />
}

function ProfilePage(pageProps: ProfilePageProps) {
  const router = useRouter()
  const handle = singleParam(router.query.handle)

  // The OG tags must stay at the page level: MainLayout only renders its
  // children client-side (viewSize gate), so head tags inside a view
  // component would never reach the SSR response
  const profileData = pageProps.profileData
  let ogImage: string | undefined
  if (profileData?.recentGuideImage) {
    ogImage = cloudflareLoader({
      src: profileData.recentGuideImage,
      width: IMAGE_SIZES.xl,
    })
  } else if (profileData?.recentCollectionImage) {
    ogImage = cloudflareLoader({
      src: profileData.recentCollectionImage,
      width: IMAGE_SIZES.xl,
    })
  }

  const ogTags = profileData ? (
    <>
      <title>{`${profileData.user.first_name ? `${userDisplayName(profileData.user, true)}` : `@${profileData.user.handle}`} | mindtrip`}</title>
      <meta
        property="og:title"
        content={`${profileData.user.first_name ? `${userDisplayName(profileData.user, true)}` : `@${profileData.user.handle}`} | mindtrip`}
      />
      <meta
        property="og:description"
        content={
          profileData.userProfile.bio ||
          `${userDisplayName(profileData.user, true)}'s profile on Mindtrip`
        }
      />
      {ogImage && <meta property="og:image" content={ogImage} key="og-image" />}
    </>
  ) : null

  return (
    <>
      <Head>{ogTags}</Head>
      <MainLayout>
        {handle &&
          (pageProps.isOwnProfile ? (
            <OwnProfileView key={handle} user={pageProps.session.user} />
          ) : (
            <PublicProfileView
              key={handle}
              handle={handle}
              profileData={profileData}
            />
          ))}
      </MainLayout>
    </>
  )
}

export const getServerSideProps = MtSSP.asAuthPage<ProfilePageProps>({
  handler: async ({ baseProps, serverAxios, context }) => {
    const handle = singleParam(context.query.handle)

    const redirect = await handleCreatorAttributionRedirect(
      context,
      serverAxios
    )

    if (redirect) return redirect

    if (handle) {
      const userApi = new UserApi(serverAxios)

      const [publicUserExists, appProps] = await Promise.all([
        // fetchPublicUser rejects with a 404 when the handle doesn't exist
        userApi.fetchPublicUser(handle).then(
          () => true,
          () => false
        ),
        baseProps(),
      ])

      if (!publicUserExists) {
        return {
          notFound: true,
        }
      }

      const isOwnProfile = appProps.session.user.handle === handle

      // Fetch profile data for OG tags and to seed the client-side store
      let profileData: InitPublicProfileProps | null = null
      if (!isOwnProfile) {
        try {
          profileData = await fetchPublicProfileProps(serverAxios, handle)
        } catch (e) {
          // If profile data fetch fails, continue without OG data
          console.error("Failed to fetch profile data for OG tags:", e)
        }
      }

      return {
        props: {
          ...appProps,
          profileData,
          isOwnProfile,
        },
      }
    }

    return {
      notFound: true,
    }
  },
})

ProfilePage.getLayout = getMainLayout

export default ProfilePage
